import React from 'react';

/**
 * THE paperclip glyph. One path, every surface.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🔀 EXTRACTED 2026-08-11, VERBATIM, from FOUR existing copies of the same 24×24
 * heroicons `paper-clip` path — `components/OrderAttachments.js` (twice: the
 * saved-file row and the staged-file row), `components/OrderForm.js`'s per-item
 * Attach button, and `components/teams/TaskFileUpload.js`'s mobile button. The
 * order list's new attachment marker would have been a FIFTH, which is the
 * Code Unification Principle's threshold, not a matter of taste: *"If it doesn't
 * [exist] but the same thing exists in another file, extract it to a shared
 * module FIRST, then import."*
 *
 * 🔑 ALL FOUR ORIGINAL CALL SITES WERE MIGRATED IN THE SAME COMMIT. An
 * extraction that leaves the copies in place is a fifth definition with a nicer
 * name — strictly worse than the duplication it claims to fix, because the next
 * reader now has to check five places instead of four to know whether they
 * agree.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SIZE AND COLOUR STAY WITH THE CALLER, DELIBERATELY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `className` is the ONLY styling input and it has no default, because the four
 * inherited sites do not agree and none of them is wrong:
 *
 *   OrderAttachments   `h-3.5 w-3.5 text-gray-400 flex-shrink-0`   (in a file row)
 *   OrderForm          `h-4 w-4 text-gray-400 hover:text-blue-500` (a button)
 *   TaskFileUpload     `w-4 h-4`                                   (inherits the button's colour)
 *
 * A default would silently restyle whichever site did not pass one. 🪤 Note
 * `strokeWidth={2}` on three of them and the fourth relying on the same — it is
 * baked in here rather than exposed, because a paperclip at a different stroke
 * weight beside one at 2 reads as a different icon, and no caller wanted that.
 *
 * ♿ IT IS PURELY DECORATIVE BY DEFAULT and carries `aria-hidden="true"`: at
 * every inherited site the meaning is carried by adjacent text or by the
 * wrapping button's own `aria-label`, so an unlabelled `<svg>` in the
 * accessibility tree is noise. **A caller that uses the clip as the SOLE carrier
 * of meaning must label its own wrapper** — `role="img"` plus an `aria-label`
 * naming the state and the number, the contract `components/LeadTimeRisk.js`'s
 * `LeadTimeDot` already follows. 🚨 Such a caller must NOT reach for a native
 * `title` on this element: a `title` on a CHILD beats its parent's, so a clip
 * inside a cell that already has one would shadow the cell tooltip on exactly
 * the pixels a user aims at. That is a measured defect in this repo, recorded
 * against `LeadTimeDot` and the unified order-table Date-cell tooltip.
 */
function PaperclipIcon({ className = '', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );
}

export default PaperclipIcon;
